import { useEffect, useMemo, useState } from 'react'

import { ruleTargets, type PriceBook } from '@iws/theme-schema/campaign'

import { ProductVariationModel } from '@/http/models/Product/ProductVariation'
import { OfferModel, ProductVariationModel as VariationType } from '@/types'

export interface OfferCatalog {
  prices: PriceBook
  stock: { byProduct: Record<string, number>; byVariation: Record<string, number> }
  hasImages: Record<string, boolean>
  loading: boolean
}

const stockOf = (v: VariationType.ProductVariation) =>
  v.stocks?.length ? v.stocks.reduce((sum, s) => sum + (s.quantity || 0), 0) : (v.initial_stock_quantity ?? 0)

/**
 * Prices, stock and images of the products an offer's rules refer to, for the live price example and the
 * pre-launch checklist. A product's price and stock are its first variation's, as on the storefront.
 */
export const useOfferCatalog = (rules: Partial<OfferModel.OfferRules>): OfferCatalog => {
  const productIds = useMemo(() => {
    const r = rules as Record<string, unknown>
    const ids = new Set(ruleTargets(rules).productIds)
    // Combo items and get-items carry their product id in the editor only.
    for (const i of (r.items as { product_id?: string }[] | undefined) ?? []) if (i.product_id) ids.add(i.product_id)
    for (const g of (r.get_items as { product_id?: string }[] | undefined) ?? []) if (g.product_id) ids.add(g.product_id)
    return [...ids].sort()
  }, [rules])
  const key = productIds.join(',')
  const [variations, setVariations] = useState<Record<string, VariationType.ProductVariation[]>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const missing = productIds.filter((id) => !(id in variations))
    if (!missing.length) return
    let cancelled = false
    setLoading(true)
    Promise.all(
      missing.map(async (productId) => {
        const res = await ProductVariationModel.getList({ params: { productId } })
        return [productId, res.success ? ((res.data?.result ?? []) as VariationType.ProductVariation[]) : []] as const
      }),
    ).then((entries) => {
      if (cancelled) return
      setVariations((prev) => ({ ...prev, ...Object.fromEntries(entries) }))
      setLoading(false)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return useMemo(() => {
    const catalog: OfferCatalog = {
      prices: { byProduct: {}, byVariation: {} },
      stock: { byProduct: {}, byVariation: {} },
      hasImages: {},
      loading,
    }
    for (const [productId, list] of Object.entries(variations)) {
      const first = list[0]
      if (first) {
        catalog.prices.byProduct[productId] = first.regular_price
        catalog.stock.byProduct[productId] = stockOf(first)
      }
      catalog.hasImages[productId] = list.some((v) => (v.images?.length ?? 0) > 0 || (v.image_ids?.length ?? 0) > 0)
      for (const v of list) {
        catalog.prices.byVariation[v._id] = v.regular_price
        catalog.stock.byVariation[v._id] = stockOf(v)
      }
    }
    return catalog
  }, [variations, loading])
}
